import {
    COMMON_COVERAGE_CONFIG,
    CoverageCheckResponseType,
} from "@common-shared/config/coverage.config"
import {
    AgenticCheckStatus,
    OneVerifyFlagType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { COMMON_ONE_VERIFY_GENERAL_CONFIG } from "@common-shared/config/oneVerify/oneVerifyGeneral.config"
import { IPolicyFlagMappingConfiguration } from "@common-shared/types/policies.types"

import { PracticeWorkflowManagementVersion } from "../practice.config"

export enum OneVerifyCommentType {
    USER = "USER",
    SYSTEM = "SYSTEM",
}

export enum OneVerifyOverrideCommentOperation {
    CREATE = "CREATE",
    UPDATE = "UPDATE",
    DELETE = "DELETE",
}

export enum OneVerifyCommentRenderingType {
    GENERAL = "GENERAL",
    OVERRIDE = "OVERRIDE",
}

export enum OneVerifyEhrWriteStatus {
    PENDING = "PENDING", // Set by us; indicates write was accepted by EHR Bridge
    CREATED = "CREATED", // Set by webhook; indicates EHR Bridge has created the policy in EHR
    ELIGIBILITY_STARTED = "ELIGIBILITY_STARTED", // Set by webhook; indicates EHR Bridge has started the eligibility check
    ELIGIBILITY_COMPLETED = "ELIGIBILITY_COMPLETED", // Set by webhook; indicates EHR Bridge has completed the eligibility check
    COMPLETED = "COMPLETED", // Set by webhook; indicates EHR Bridge has completed the write-back (terminal status)
    FAILED = "FAILED", // Set by webhook; indicates EHR Bridge has failed the write-back (terminal status)
}

export enum PolicyDenialRisk {
    HIGH = "HIGH",
    MEDIUM = "MEDIUM",
    LOW = "LOW",
    NONE = "NONE",
}

export enum PoliciesListType {
    HISTORY_SEARCH = "HISTORY_SEARCH",
    REPORTS = "REPORTS",
    OVERRIDES = "OVERRIDES",
    PLAN_YEAR_RESETS = "PLAN_YEAR_RESETS",
    MEDICAID = "MEDICAID",
    AGENTIC = "AGENTIC",
    ASSIGNED_TASKS = "ASSIGNED_TASKS",
    TASK_ASSIGNMENTS = "TASK_ASSIGNMENTS",
    ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS = "ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS",
    TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS = "TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS",
    ASSIGNED_TASKS_MEDICAID = "ASSIGNED_TASKS_MEDICAID",
    TASK_ASSIGNMENTS_MEDICAID = "TASK_ASSIGNMENTS_MEDICAID",
}

export enum PolicyModality {
    MENTAL_HEALTH = "MENTAL_HEALTH",
    PHYSICAL_THERAPY = "PHYSICAL_THERAPY",
    EVALUATION_MANAGEMENT = "EVALUATION_MANAGEMENT", // PRIMARY_CARE called in labeling portal
    MEDICAL_NUTRITION_THERAPY = "MEDICAL_NUTRITION_THERAPY",
    HOME_SLEEP_THERAPY = "HOME_SLEEP_THERAPY",
    MRI = "MRI",
    COMPUTED_TOMOGRAPHY = "COMPUTED_TOMOGRAPHY",
    INTENSIVE_OUTPATIENT_PROGRAM = "INTENSIVE_OUTPATIENT_PROGRAM",
    PARTIAL_HOSPITALIZATION_PROGRAM = "PARTIAL_HOSPITALIZATION_PROGRAM",
    APPLIED_BEHAVIORAL_ANALYSIS = "APPLIED_BEHAVIORAL_ANALYSIS",
    SUBSTANCE_USE_DISORDER = "SUBSTANCE_USE_DISORDER",
    OBSTETRICS_GYNECOLOGY = "OBSTETRICS_GYNECOLOGY",
    MATERNITY = "MATERNITY",
    ORTHOPEDICS = "ORTHOPEDICS",
    OCCUPATIONAL_THERAPY = "OCCUPATIONAL_THERAPY",
    SPEECH_THERAPY = "SPEECH_THERAPY",
    URGENT_CARE = "URGENT_CARE",
    GASTROENTEROLOGY = "GASTROENTEROLOGY",
    DERMATOLOGY = "DERMATOLOGY",
    CARDIOLOGY = "CARDIOLOGY",
    ENDOCRINOLOGY = "ENDOCRINOLOGY",
    NEPHROLOGY = "NEPHROLOGY",
    NEUROLOGY = "NEUROLOGY",
    PULMONOLOGY = "PULMONOLOGY",
    UROLOGY = "UROLOGY",
    CHRONIC_CARE_MANAGEMENT = "CHRONIC_CARE_MANAGEMENT",
    LABORATORY = "LABORATORY",
}

export enum PolicyModalityCode {
    PSY = "PSY", // Psychotherapy / Mental Health
    PSC = "PSC", // Psychiatry
    PMC = "PMC", // Primary Care
    PHT = "PHT", // Physical Therapy
    MRI = "MRI", // MRI
    HST = "HST", // Home Sleep Therapy
    MNT = "MNT", // Medical Nutrition Therapy
    CT = "CT", // Computed Tomography
    IOP = "IOP", // Intensive Outpatient Program
    PHP = "PHP", // Partial Hospitalization Program
    ABA = "ABA", // Applied Behavioral Analysis
    SUD = "SUD", // Substance Use Disorder
    OBG = "OBG", // Obstetrics and Gynecology
    MAT = "MAT", // Maternity
    ORP = "ORP", // Orthopedics
    OT = "OT", // Occupational Therapy
    ST = "ST", // Speech Therapy
    UC = "UC", // Urgent Care
    GI = "GI", // Gastroenterology
    DER = "DER", // Dermatology
    CAR = "CAR", // Cardiology
    END = "END", // Endocrinology
    NEP = "NEP", // Nephrology
    NEU = "NEU", // Neurology
    PUL = "PUL", // Pulmonology
    URO = "URO", // Urology
    CCM = "CCM", // Chronic Care Management
    LAB = "LAB", // Laboratory Testing
}

export enum PolicyAssignmentStatus {
    NEW = "NEW",
    IN_PROGRESS = "IN_PROGRESS",
    BLOCKED = "BLOCKED",
    BLOCKED_COVERAGE_ISSUE = "BLOCKED_COVERAGE_ISSUE",
    APPOINTMENT_CANCELLED = "APPOINTMENT_CANCELLED",
    CANCELLED = "CANCELLED", // used when unassigned or re-assigned (assignment is cancelled itself), internal usage only
    DONE = "DONE",
    DONE_SELF_PAY = "DONE_SELF_PAY",
}

export enum PolicyAssignmentAppointmentType {
    THER_IA_ADULT = "THER_IA_ADULT",
    THER_IA_CHILD = "THER_IA_CHILD",
    TELE_THER_IA_ADULT = "TELE_THER_IA_ADULT",
    TELE_THER_IA_CHILD = "TELE_THER_IA_CHILD",
    MED_IA_ADULT = "MED_IA_ADULT",
    MED_IA_CHILD = "MED_IA_CHILD",
    TELE_MED_IA_ADULT = "TELE_MED_IA_ADULT",
    TELE_MED_IA_CHILD = "TELE_MED_IA_CHILD",
}

export enum PolicyAssignmentAppointmentStatus {
    MADE = "MADE",
    ARRIVED = "ARRIVED",
    OTHER = "OTHER",
    SEEN = "SEEN",
    MOVED = "MOVED",
    CANCELLED = "CANCELLED",
    DELETED = "DELETED",
    NO_SHOW = "NO_SHOW",
}

export enum PolicyReassignmentReason {
    OPERATIONAL_EXCEPTION = "OPERATIONAL_EXCEPTION",
    PRODUCTIVITY_GAP = "PRODUCTIVITY_GAP",
}

export enum PolicyRangeDateFilterType {
    TODAY = "TODAY",
    DAY_BEFORE = "DAY_BEFORE",
    DAY_AFTER = "DAY_AFTER",
    LAST_X_DAYS = "LAST_X_DAYS",
    NEXT_X_DAYS = "NEXT_X_DAYS",
    CUSTOM_RANGE = "CUSTOM_RANGE",
}

export enum PoliciesExportDestination {
    CSV = "csv",
    EMAIL = "email",
}

export enum PolicyAssignmentWorkflowStatus {
    FULLY_AUTOMATED = "FULLY_AUTOMATED",
    PARTIALLY_AUTOMATED = "PARTIALLY_AUTOMATED",
    NOT_AUTOMATED = "NOT_AUTOMATED",
}

export enum PolicyAssignmentPriority {
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    HIGH = "HIGH",
}

export enum PolicyCredentialingStatus {
    BOOK_AND_HOLD = "Book & Hold",
    INN_NO_CRED_REQUIRED = "INN-No Cred Required",
    IN_NETWORK = "In Network",
    INCIDENT_TO = "Incident To",
    INCIDENT_TO_BILLING = "Incident To Billing",
    NO_ACTIVE_ENROLLMENT_STATUS_FOUND = "No active enrollment status found",
    NO_CREDENTIALING_DATA_FOUND = "No credentialing data found",
    PENDING = "Pending",
    SUPERVISED = "Supervised",
}

export enum PolicyAssignability {
    ASSIGNABLE = "ASSIGNABLE",
    UNASSIGNABLE = "UNASSIGNABLE",
}

export const COMMON_ONE_VERIFY_POLICIES_CONFIG = {
    planYearResetsTargetYear: 2027,
    planYearResetsTargetMonth: "January",
    planYearResetsTargetMonthAbbrv: "Jan",

    mediumAttentionFlagSeverityCheck: 40,
    highAttentionFlagSeverityCheck: 70,

    customFieldPrefixFront: "customField",
    customFieldPrefixDb: "custom_fields",

    networkStatusAlertTriggeredLabel: "Triggered",

    ehrWriteAllowedResponseStatusCodes: [200, 206],

    ehrWriteDisabledListTypes: [
        PoliciesListType.AGENTIC,
        PoliciesListType.PLAN_YEAR_RESETS,
    ],

    defaultAssignmentPriority: PolicyAssignmentPriority.MEDIUM,

    assignmentsProductivityExport: {
        defaultChunkSize: 200,
        maxChunkSize: 400,
        assignmentDateLookbackDays: 7,
    },

    get flagTypeMappings(): Record<
        OneVerifyFlagType,
        IPolicyFlagMappingConfiguration
    > {
        return {
            [OneVerifyFlagType.INACTIVE_POLICY]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.INACTIVE_POLICY
                    ],
                label: "Policy Expired",
                tooltip: "Detected policy has expired.",
            },
            [OneVerifyFlagType.POLICY_NOT_FOUND]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.POLICY_NOT_FOUND
                    ],
                label: "Policy Not Found",
                tooltip:
                    "Payer does not recognize the member. Confirm that the payer and member information are correct and resubmit.",
            },
            [OneVerifyFlagType.VISITS_MET]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.VISITS_MET
                    ],
                label: "Visit Limit Met",
                tooltip: "The number of visits allowed has been exhausted.",
            },
            [OneVerifyFlagType.REVIEW_REQUIRED]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.REVIEW_REQUIRED
                    ],
                label: "Review Required",
                tooltip: "Review individual policy for specific issue.",
            },
            [OneVerifyFlagType.MEMBER_ID_MISMATCH]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.MEMBER_ID_MISMATCH
                    ],
                label: "Member ID Mismatch",
                tooltip:
                    "Different member ID detected. Confirm with the patient and update.",
            },
            [OneVerifyFlagType.MEMBER_DOB_MISMATCH]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.MEMBER_DOB_MISMATCH
                    ],
                label: "Member DOB Mismatch",
                tooltip:
                    "Different member DOB detected. Confirm with the patient and update.",
            },
            [OneVerifyFlagType.CREDENTIALING_ERROR]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.CREDENTIALING_ERROR
                    ],
                label: "Credentialing Error",
                tooltip:
                    "Payer does not recognize the provider and provider has to go through the credentialing process.",
            },
            [OneVerifyFlagType.ENROLLMENT_REQUIRED]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.ENROLLMENT_REQUIRED
                    ],
                label: "Enrollment Required",
                tooltip:
                    "Payer does not have enrollment with NPI. Contact Nirvana to assist in enrolling provider.",
            },
            [OneVerifyFlagType.THIRD_PARTY_PAYER_DETECTED]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.THIRD_PARTY_PAYER_DETECTED
                    ],
                label: "Third Party Payer Detected",
                tooltip: "Third party payer detected.",
            },
            ///////////////////////////////////////////////////// END HIGH, START MEDIUM severity
            [OneVerifyFlagType.EXPIRING_SOON]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.EXPIRING_SOON
                    ],
                label: "Policy Expires Soon",
                tooltip: "The policy is expiring within 30 days",
            },
            [OneVerifyFlagType.UNKNOWN_VISITS]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.UNKNOWN_VISITS
                    ],
                label: "Unknown Visits",
                tooltip: "Unable to determine visit limit.",
            },
            [OneVerifyFlagType.DEMOGRAPHICS_MISMATCH]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.DEMOGRAPHICS_MISMATCH
                    ],
                label: "Demographics Mismatch",
                tooltip:
                    "Different demographic details detected. Confirm with the patient and update.",
            },
            [OneVerifyFlagType.MEDICARE_ADVANTAGE]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.MEDICARE_ADVANTAGE
                    ],
                label: "Medicare Advantage",
                tooltip: "Medicare Advantage plan type detected.",
            },
            [OneVerifyFlagType.MANAGED_MEDICAID]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.MANAGED_MEDICAID
                    ],
                label: "Managed Medicaid",
                tooltip: "Managed Medicaid plan type detected",
            },
            [OneVerifyFlagType.ADDITIONAL_POLICY_DETECTED]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.ADDITIONAL_POLICY_DETECTED
                    ],
                label: "Additional Policy",
                tooltip:
                    "Coordination of benefits: additional policy detected.",
            },
            ///////////////////////////////////////////////////// END MEDIUM, START LOW severity
            [OneVerifyFlagType.QUALIFIED_MEDICARE_BENEFICIARY]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.QUALIFIED_MEDICARE_BENEFICIARY
                    ],
                label: "Qualified Medicare Beneficiary",
                tooltip:
                    "Patient is a Qualified Medicare Beneficiary and should not be billed.",
            },
            [OneVerifyFlagType.UNKNOWN_COSTSHARE]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.UNKNOWN_COSTSHARE
                    ],
                label: "Unknown Costshare",
                tooltip: "Unable to determine copayment or coinsurance.",
            },
            [OneVerifyFlagType.UNKNOWN_ACCUMULATOR]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.UNKNOWN_ACCUMULATOR
                    ],
                label: "Unknown Accumulators",
                tooltip:
                    "Unable to determine deductible, out-of-pocket max, and/or remainings.",
            },
            [OneVerifyFlagType.UNKNOWN_MEMBER_OBLIGATION]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.UNKNOWN_MEMBER_OBLIGATION
                    ],
                label: "Unknown Member Obligation",
                tooltip: "Copayment cannot be greater than session rate.",
            },
            [OneVerifyFlagType.OOP_MAX_MET]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.OOP_MAX_MET
                    ],
                label: "OOP Max Met",
                tooltip: "Patient's out of pocket maximum has been reached.",
            },
            [OneVerifyFlagType.DEDUCTIBLE_MET]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.DEDUCTIBLE_MET
                    ],
                label: "Deductible Met",
                tooltip: "Patient's deductible has been reached.",
            },
            [OneVerifyFlagType.NEW_PATIENT]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.NEW_PATIENT
                    ],
                label: "New Patient",
                tooltip: "New patient for practice",
            },
            [OneVerifyFlagType.TELEHEALTH_COST_SHARE_DIFFERENCES]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.TELEHEALTH_COST_SHARE_DIFFERENCES
                    ],
                label: "Telehealth Cost Share Differences",
                tooltip:
                    "Telehealth and office visits have different copay / coinsurance.",
            },
            [OneVerifyFlagType.NETWORK_STATUS_MISMATCH]: {
                severity:
                    COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity[
                        OneVerifyFlagType.NETWORK_STATUS_MISMATCH
                    ],
                label: "Network Status Mismatch",
                tooltip: "Network status mismatch detected.",
            },
        }
    },

    get denialRiskMapping(): Record<PolicyDenialRisk, string> {
        return {
            [PolicyDenialRisk.HIGH]: "High",
            [PolicyDenialRisk.MEDIUM]: "Medium",
            [PolicyDenialRisk.LOW]: "Low",
            [PolicyDenialRisk.NONE]: "None",
        }
    },

    get agenticCheckStatusMapping(): Record<AgenticCheckStatus, string> {
        return {
            [AgenticCheckStatus.NEW]: "Agent phone call in progress",
            [AgenticCheckStatus.PENDING]: "Agent phone call in progress",
            [AgenticCheckStatus.FINISHED]: "Agent check finished",
            [AgenticCheckStatus.FAILED]: "Agent check failed",
        }
    },

    policyAssignmentStatusMapping(
        listType: PoliciesListType,
    ): Partial<Record<PolicyAssignmentStatus, string>> {
        switch (listType) {
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
                return {
                    [PolicyAssignmentStatus.NEW]: "Not Started",
                    [PolicyAssignmentStatus.IN_PROGRESS]: "In Progress",
                    [PolicyAssignmentStatus.BLOCKED]: "Follow Up Needed",
                    [PolicyAssignmentStatus.BLOCKED_COVERAGE_ISSUE]:
                        "Coverage Issue",
                    [PolicyAssignmentStatus.APPOINTMENT_CANCELLED]:
                        "Appointment Cancelled",
                    [PolicyAssignmentStatus.CANCELLED]: "Assignment Cancelled",
                    [PolicyAssignmentStatus.DONE]:
                        "Completed Verification In Chart",
                    [PolicyAssignmentStatus.DONE_SELF_PAY]: "Self Pay",
                }

            default:
                return {
                    [PolicyAssignmentStatus.NEW]: "Not Started",
                    [PolicyAssignmentStatus.IN_PROGRESS]: "In Progress",
                    [PolicyAssignmentStatus.BLOCKED]: "Blocked",
                    [PolicyAssignmentStatus.CANCELLED]: "Cancelled",
                    [PolicyAssignmentStatus.DONE]: "Done",
                }
        }
    },

    get policyAssignmentAppointmentTypeMapping(): Record<
        PolicyAssignmentAppointmentType,
        string
    > {
        return {
            [PolicyAssignmentAppointmentType.THER_IA_ADULT]: "Ther IA Adult",
            [PolicyAssignmentAppointmentType.THER_IA_CHILD]: "Ther IA Child",
            [PolicyAssignmentAppointmentType.TELE_THER_IA_ADULT]:
                "Tele Ther IA Adult",
            [PolicyAssignmentAppointmentType.TELE_THER_IA_CHILD]:
                "Tele Ther IA Child",
            [PolicyAssignmentAppointmentType.MED_IA_ADULT]: "Med IA Adult",
            [PolicyAssignmentAppointmentType.MED_IA_CHILD]: "Med IA Child",
            [PolicyAssignmentAppointmentType.TELE_MED_IA_ADULT]:
                "Tele Med IA Adult",
            [PolicyAssignmentAppointmentType.TELE_MED_IA_CHILD]:
                "Tele Med IA Child",
        }
    },

    get uiAllowedAssignmentStatuses(): PolicyAssignmentStatus[] {
        return Object.values(PolicyAssignmentStatus).filter(item => {
            return !COMMON_ONE_VERIFY_POLICIES_CONFIG.internalAssignmentStatuses.includes(
                item,
            )
        })
    },

    get activeAssignmentStatuses(): PolicyAssignmentStatus[] {
        return Object.values(PolicyAssignmentStatus).filter(item => {
            return (
                !COMMON_ONE_VERIFY_POLICIES_CONFIG.doneAssignmentStatuses.includes(
                    item,
                ) &&
                !COMMON_ONE_VERIFY_POLICIES_CONFIG.internalAssignmentStatuses.includes(
                    item,
                )
            )
        })
    },

    get doneAssignmentStatuses(): PolicyAssignmentStatus[] {
        return [
            PolicyAssignmentStatus.APPOINTMENT_CANCELLED,
            PolicyAssignmentStatus.DONE,
            PolicyAssignmentStatus.DONE_SELF_PAY,
        ]
    },

    get internalAssignmentStatuses(): PolicyAssignmentStatus[] {
        return [PolicyAssignmentStatus.CANCELLED]
    },

    get policyReassignmentReasonMapping(): Record<
        PolicyReassignmentReason,
        string
    > {
        return {
            [PolicyReassignmentReason.OPERATIONAL_EXCEPTION]:
                "Operational Exception",
            [PolicyReassignmentReason.PRODUCTIVITY_GAP]: "Productivity Gap",
        }
    },

    get policyAssignmentWorkflowStatusMapping(): Record<
        PolicyAssignmentWorkflowStatus,
        string
    > {
        return {
            [PolicyAssignmentWorkflowStatus.FULLY_AUTOMATED]: "Fully automated",
            [PolicyAssignmentWorkflowStatus.PARTIALLY_AUTOMATED]:
                "Partially automated",
            [PolicyAssignmentWorkflowStatus.NOT_AUTOMATED]: "Not automated",
        }
    },

    get policyAssignabilityMapping(): Record<PolicyAssignability, string> {
        return {
            [PolicyAssignability.ASSIGNABLE]: "Assignable",
            [PolicyAssignability.UNASSIGNABLE]: "Unassignable",
        }
    },

    get tasksListTypes(): PoliciesListType[] {
        return [
            ...COMMON_ONE_VERIFY_POLICIES_CONFIG.assignedTasksListTypes,
            ...COMMON_ONE_VERIFY_POLICIES_CONFIG.taskAssignmentsListTypes,
        ]
    },

    get assignedTasksListTypes(): PoliciesListType[] {
        return [
            PoliciesListType.ASSIGNED_TASKS,
            PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS,
            PoliciesListType.ASSIGNED_TASKS_MEDICAID,
        ]
    },

    get taskAssignmentsListTypes(): PoliciesListType[] {
        return [
            PoliciesListType.TASK_ASSIGNMENTS,
            PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS,
            PoliciesListType.TASK_ASSIGNMENTS_MEDICAID,
        ]
    },

    get oneVerifyEhrWriteStatusDisplay(): Record<
        OneVerifyEhrWriteStatus,
        string
    > {
        return {
            [OneVerifyEhrWriteStatus.PENDING]: "Pending",
            [OneVerifyEhrWriteStatus.CREATED]: "Pending",
            [OneVerifyEhrWriteStatus.ELIGIBILITY_STARTED]: "Pending",
            [OneVerifyEhrWriteStatus.ELIGIBILITY_COMPLETED]: "Pending",
            [OneVerifyEhrWriteStatus.COMPLETED]: "Completed",
            [OneVerifyEhrWriteStatus.FAILED]: "Failed",
        }
    },

    get oneVerifyEhrWriteTerminalStatuses(): OneVerifyEhrWriteStatus[] {
        return [
            OneVerifyEhrWriteStatus.COMPLETED,
            OneVerifyEhrWriteStatus.FAILED,
        ]
    },

    // Ignore default response types during export for some list types
    // Because they have UI control for selecting response types and it would be confusing to have default ones applied during export in that case
    get listTypesIgnoreDefaultResponseTypeDuringExport(): PoliciesListType[] {
        return [PoliciesListType.HISTORY_SEARCH, PoliciesListType.REPORTS]
    },

    get defaultResponseTypesByListType(): Record<
        PoliciesListType,
        CoverageCheckResponseType | CoverageCheckResponseType[]
    > {
        return {
            [PoliciesListType.HISTORY_SEARCH]:
                COMMON_COVERAGE_CONFIG.mainCoverageResponseTypes,
            [PoliciesListType.REPORTS]:
                COMMON_COVERAGE_CONFIG.mainCoverageResponseTypes,
            [PoliciesListType.MEDICAID]: CoverageCheckResponseType.MEDICAID,
            [PoliciesListType.OVERRIDES]:
                COMMON_COVERAGE_CONFIG.mainCoverageResponseTypes,
            [PoliciesListType.PLAN_YEAR_RESETS]:
                CoverageCheckResponseType.COVERAGE,
            [PoliciesListType.AGENTIC]: CoverageCheckResponseType.COVERAGE,
            [PoliciesListType.ASSIGNED_TASKS]:
                CoverageCheckResponseType.COVERAGE,
            [PoliciesListType.TASK_ASSIGNMENTS]:
                CoverageCheckResponseType.COVERAGE,
            [PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS]:
                CoverageCheckResponseType.COVERAGE,
            [PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS]:
                CoverageCheckResponseType.COVERAGE,
            [PoliciesListType.ASSIGNED_TASKS_MEDICAID]:
                COMMON_COVERAGE_CONFIG.mainCoverageResponseTypes,
            [PoliciesListType.TASK_ASSIGNMENTS_MEDICAID]:
                COMMON_COVERAGE_CONFIG.mainCoverageResponseTypes,
        }
    },

    get taskAssignmentsListTypeVariant(): Record<
        PracticeWorkflowManagementVersion,
        PoliciesListType
    > {
        return {
            [PracticeWorkflowManagementVersion.DEFAULT]:
                PoliciesListType.TASK_ASSIGNMENTS,
            [PracticeWorkflowManagementVersion.EXTENDED_CUSTOM_FIELDS]:
                PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS,
            [PracticeWorkflowManagementVersion.MEDICAID]:
                PoliciesListType.TASK_ASSIGNMENTS_MEDICAID,
        }
    },

    get assignedTasksListTypeVariant(): Record<
        PracticeWorkflowManagementVersion,
        PoliciesListType
    > {
        return {
            [PracticeWorkflowManagementVersion.DEFAULT]:
                PoliciesListType.ASSIGNED_TASKS,
            [PracticeWorkflowManagementVersion.EXTENDED_CUSTOM_FIELDS]:
                PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS,
            [PracticeWorkflowManagementVersion.MEDICAID]:
                PoliciesListType.ASSIGNED_TASKS_MEDICAID,
        }
    },
}
